import { ethers } from 'ethers';
import { useGasPrice, getGasStatus } from '@/hooks/use-gas-price';
import { Skeleton } from '@/components/ui/skeleton';
import { motion, AnimatePresence } from 'framer-motion';

interface GasPriceIndicatorProps {
  provider?: ethers.Provider;
  className?: string;
}

// Component for animated number
function AnimatedNumber({ value, className = '' }: { value: string; className?: string }) {
  return (
    <motion.span
      key={value}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.3 }}
      className={className}
    >
      {value}
    </motion.span>
  );
}

export function GasPriceIndicator({ provider, className = '' }: GasPriceIndicatorProps) {
  const { gasPrice, estimatedTransactionCost, isLoading, error } = useGasPrice(provider);
  const gasStatus = getGasStatus(gasPrice);

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Skeleton className="h-3 w-3 rounded-full" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-12" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center gap-2 text-gray-500 text-xs ${className}`}>
        <div className="w-3 h-3 rounded-full bg-gray-400"></div>
        <span>Gas: Unavailable</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Color circle indicator */}
      <div className={`w-3 h-3 rounded-full ${gasStatus.bgColor}`}></div>
      
      <span className="text-gray-600 text-xs">Gas:</span>
      <span className={`text-xs ${gasStatus.color}`}>
        {gasStatus.label}
      </span>
      <AnimatePresence mode="wait">
        <AnimatedNumber 
          value={`${parseFloat(gasPrice).toFixed(1)} gwei`}
          className="text-gray-500 text-xs"
        />
      </AnimatePresence>
      <AnimatePresence mode="wait">
        <AnimatedNumber 
          value={`(~${parseFloat(estimatedTransactionCost).toFixed(4)} ETH)`}
          className="text-gray-400 text-xs"
        />
      </AnimatePresence>
    </div>
  );
} 